/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import { ITextProps, Text } from '@fluentui/react/lib/Text';
import { Property } from 'csstype';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getStylist } from 'src/core/themes/luisStylist';
import { noop } from 'src/core/utils/base';
import { DataImbalanceProblem } from 'src/luis/projects/analytics/components/OverallPredictionsCard/ProblemsSection/DataImbalanceProblem';
import { IncorrectProblem } from 'src/luis/projects/analytics/components/OverallPredictionsCard/ProblemsSection/IncorrectProblem';
import { UnclearProblem } from 'src/luis/projects/analytics/components/OverallPredictionsCard/ProblemsSection/UnclearProblem';
import { IntentMetadata } from 'src/luis/projects/analytics/types/IntentMetadata';
import { PassedFilters } from 'src/luis/projects/analytics/types/PassedFilters';
import { LuisId } from 'src/luis/types/Resource';

export type ProblemsSectionProps = {
    /**
     * The list of intents with low number of utterances.
     */
    needyIntents: IntentMetadata[];

    /**
     * The list of intents with high number of utterances.
     */
    greedyIntents: IntentMetadata[];

    /**
     * The list of intents with incorrect utterances.
     */
    incorrectIntents: IntentMetadata[];

    /**
     * The list of intents with unclear utterances.
     */
    unclearIntents: IntentMetadata[];

    /**
     * A callback for when an intent is clicked.
     */
    onIntentClick: (intentId: LuisId, filters: PassedFilters) => void;
};

const messages = defineMessages({
    title: {
        id: 'analytics.overallPredictionsCard.title',
        defaultMessage: 'Problems and suggestions'
    }
});

const { styleComponent } = getStylist('Dashboard');

const Title = styleComponent<ITextProps>(Text)('Title', theme => ({
    textTransform: 'uppercase',
    color: theme.defaultTextColor,
    marginBottom: theme.titleSpacing,
    lineHeight: theme.mediumLineHeight,
    letterSpacing: theme.xLargeLetterSpacing,
    fontWeight: theme.boldFont as Property.FontWeight
}));

/**
 * Represents the problem section in the overall predictions card.
 *
 * @param props The different problematic intent lists.
 */
export const ProblemsSection = (props: ProblemsSectionProps) => {
    const { needyIntents, greedyIntents, incorrectIntents, unclearIntents, onIntentClick = noop } = props;
    const { localize } = useLocalization();

    return (
        <Stack verticalAlign="center">
            {Boolean(needyIntents.length || greedyIntents.length || incorrectIntents.length || unclearIntents.length) && (
                <>
                    <Title variant="smallPlus">{localize(messages.title)}</Title>
                    <Stack tokens={{ childrenGap: 4 }}>
                        {(needyIntents.length || greedyIntents.length) && (
                            <DataImbalanceProblem
                                needyIntents={needyIntents}
                                greedyIntents={greedyIntents}
                                onIntentClick={id => onIntentClick(id, null)}
                            />
                        )}
                        {incorrectIntents.length ? (
                            <IncorrectProblem incorrectIntents={incorrectIntents} onIntentClick={onIntentClick} />
                        ) : null}
                        {unclearIntents.length ? <UnclearProblem unclearIntents={unclearIntents} onIntentClick={onIntentClick} /> : null}
                    </Stack>
                </>
            )}
        </Stack>
    );
};
